import { createAsyncThunk } from '@reduxjs/toolkit';

import { normalizeSavingsProductsResponse } from '../lib/normalizeProductsResponse';

import type {
    ProductsResponseDto,
    SavingsInsightsOrderBy,
} from '../types/ReportSavingsSummarySchema';
import type { ThunkConfig } from 'app/providers/StoreProvider/config/types';

export interface FetchSavingsProductsParams {
    dateFrom: string;
    dateTo: string;
    propertyIds?: string[];
    categoryIds?: string[];
    orderBy?: SavingsInsightsOrderBy;
    page?: number;
}

export const fetchSavingsProducts = createAsyncThunk<
    ProductsResponseDto,
    FetchSavingsProductsParams,
    ThunkConfig<string>
>(
    'reportSavingsSummary/fetchSavingsProducts',
    async (
        {
            dateFrom,
            dateTo,
            propertyIds,
            categoryIds,
            orderBy = '-total_saved',
            page = 1,
        },
        thunkApi,
    ) => {
        const { extra, rejectWithValue } = thunkApi;
        try {
            const response = await extra.api.post<ProductsResponseDto>(
                '/v1/reports/savings-insights/products',
                {
                    dateFrom,
                    dateTo,
                    orderBy,
                    page,
                    pageSize: 25,
                    excludeZeroSavings: true,
                    ...(propertyIds?.length
                        ? {
                              propertyIds,
                          }
                        : {}),
                    ...(categoryIds?.length
                        ? {
                              categoryIds,
                          }
                        : {}),
                },
            );
            return normalizeSavingsProductsResponse(response.data);
        } catch {
            return rejectWithValue('error');
        }
    },
);

export const fetchSavingsMissedProducts = createAsyncThunk<
    ProductsResponseDto,
    Omit<FetchSavingsProductsParams, 'orderBy' | 'page'>,
    ThunkConfig<string>
>(
    'reportSavingsSummary/fetchSavingsMissedProducts',
    async ({ dateFrom, dateTo, propertyIds, categoryIds }, thunkApi) => {
        const { extra, rejectWithValue } = thunkApi;
        try {
            const response = await extra.api.post<ProductsResponseDto>(
                '/v1/reports/savings-insights/products',
                {
                    dateFrom,
                    dateTo,
                    orderBy: '-missed_savings',
                    page: 1,
                    pageSize: 25,
                    excludeZeroMissedSavings: true,
                    ...(propertyIds?.length
                        ? {
                              propertyIds,
                          }
                        : {}),
                    ...(categoryIds?.length
                        ? {
                              categoryIds,
                          }
                        : {}),
                },
            );
            return normalizeSavingsProductsResponse(response.data);
        } catch {
            return rejectWithValue('error');
        }
    },
);
